import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { setAccessToken, getAccessToken } from '../services/api';

interface AuthState {
  isAuthenticated: boolean;
  login: (accessToken: string, refreshToken: string) => void;
  logout: () => void;
  /** Call on any user interaction to reset the inactivity timer */
  resetInactivityTimer: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => getAccessToken() !== null,
  );
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setIsAuthenticated(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    navigate('/admin/login');
  }, [clearSession, navigate]);

  const startInactivityTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      clearSession();
      navigate('/admin/login');
    }, INACTIVITY_TIMEOUT_MS);
  }, [clearSession, navigate]);

  const login = useCallback(
    (accessToken: string, _refreshToken: string) => {
      setAccessToken(accessToken);
      setIsAuthenticated(true);
      startInactivityTimer();
    },
    [startInactivityTimer],
  );

  const resetInactivityTimer = useCallback(() => {
    if (isAuthenticated) startInactivityTimer();
  }, [isAuthenticated, startInactivityTimer]);

  // Listen for user activity to reset the inactivity timer
  useEffect(() => {
    if (!isAuthenticated) return;
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
    const handler = () => resetInactivityTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    startInactivityTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, resetInactivityTimer, startInactivityTimer]);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, login, logout, resetInactivityTimer }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
